/**
 * API configuration.
 *
 * VITE_TENANT_DIRECTORY_URL: Control Plane endpoint for tenant list (GET /tenants).
 * VITE_API_BASE_URL: Legacy/fallback — a single Data Plane API endpoint.
 *
 * When neither is set, the app uses mock data (local development mode).
 * In production, VITE_TENANT_DIRECTORY_URL is set and the per-tenant API endpoint
 * comes from the Tenant Registry (stored in sessionStorage after selection).
 */

/** Tenant Directory (Control Plane) URL for listing tenants */
export const TENANT_DIRECTORY_URL = import.meta.env.VITE_TENANT_DIRECTORY_URL || '';

/**
 * Storage key for the selected tenant. Persisted in localStorage (not
 * sessionStorage) so the mailbox choice survives reloads and new tabs — the
 * picker is only shown the first time a user signs in.
 */
export const TENANT_STORAGE_KEY = 'mailhub:selectedTenant';

/** Read the persisted tenant record, or null when nothing is selected yet. */
export function readStoredTenant(): Record<string, unknown> | null {
  try {
    const stored = localStorage.getItem(TENANT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/** Legacy: single Data Plane API URL (used as fallback if no tenant selected) */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/** True when running in mock mode (no backend configured) — deprecated, always use real client */
// export const USE_MOCK = !API_BASE_URL && !TENANT_DIRECTORY_URL;

/**
 * Returns the API base URL for the currently selected tenant.
 * Reads the persisted tenant (set by TenantContext on selection).
 * Falls back to VITE_API_BASE_URL for backwards compat.
 */
export function getApiBaseUrl(): string {
  const tenant = readStoredTenant();
  const endpoint = tenant?.apiEndpoint;
  if (typeof endpoint === 'string' && endpoint) return endpoint.replace(/\/$/, '');
  return API_BASE_URL.replace(/\/$/, '');
}

/**
 * Dynamic mailbox ID — reads the persisted tenant (set by TenantContext on
 * selection). Falls back to VITE_MAILBOX_ID env var or 'default'.
 */
export function getMailboxId(): string {
  const tenant = readStoredTenant();
  const mailboxId = tenant?.mailboxId;
  if (typeof mailboxId === 'string' && mailboxId) return mailboxId;
  return import.meta.env.VITE_MAILBOX_ID || 'default';
}

/** @deprecated Use getMailboxId() for dynamic tenant support */
export const MAILBOX_ID = import.meta.env.VITE_MAILBOX_ID || 'default';
